import { useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { cleanLogout } from '@/lib/auth';

/**
 * Hook personalizado para manejar la autenticación con Supabase
 * 
 * Características:
 * - Gestiona el estado del usuario y la sesión
 * - Escucha cambios en el estado de autenticación en tiempo real
 * - Proporciona función para cerrar sesión
 * - Maneja estado de carga durante la inicialización
 * 
 * @returns {Object} - user: Usuario actual, session: Sesión activa, loading: Estado de carga, signOut: Función para cerrar sesión
 */
export const useAuth = () => {
  // Estado del usuario autenticado
  const [user, setUser] = useState<User | null>(null);
  
  // Estado de la sesión (contiene tokens de autenticación)
  const [session, setSession] = useState<Session | null>(null);
  
  // Estado de carga para evitar flickering en la UI
  const [loading, setLoading] = useState(true);

  const queryClient = useQueryClient();

  useEffect(() => {
    // Configurar listener para cambios en el estado de autenticación
    // Esto se ejecuta cuando el usuario inicia/cierra sesión
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // Verificar si existe una sesión activa al cargar la aplicación
    // Esto recupera la sesión del localStorage si existe
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Cleanup: desuscribirse del listener al desmontar el componente
    return () => subscription.unsubscribe();
  }, []);

  /**
   * Cierra la sesión del usuario actual y redirige al login
   */
  const signOut = async () => {
    await cleanLogout(queryClient);
  };

  return { user, session, loading, signOut };
};
